import React, { useCallback, useEffect } from 'react';
import { useEnquiry } from '../context/EnquiryContext';
import { buildWhatsAppLink } from '../data/site';
import { IconArrowLeft, IconArrowRight, IconClose, IconWhatsApp } from './ui/Icons';

/**
 * Full-screen viewer for catalogue photos.
 * Stepping through images here also feeds the enquiry gate.
 */
export default function Lightbox({ items, index, onClose, onIndex, contextLabel = '' }) {
  const { registerView } = useEnquiry();
  const item = items?.[index];

  const go = useCallback(
    (delta) => {
      if (!items?.length) return;
      onIndex((index + delta + items.length) % items.length);
    },
    [index, items, onIndex]
  );

  // Each photo opened here counts toward the enquiry gate — the prompt lands
  // on the 3rd one.
  useEffect(() => {
    if (!item) return;
    registerView(item.id, contextLabel, 'detail');
  }, [item, registerView, contextLabel]);

  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, go]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[110] flex flex-col bg-royal-900/95" role="dialog" aria-modal="true">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between px-5 py-4 sm:px-8">
        <span className="text-[13px] uppercase tracking-wider2 text-cream/75">
          {index + 1} / {items.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close viewer"
          className="flex h-10 w-10 items-center justify-center text-cream/70 transition-colors hover:text-gold"
        >
          <IconClose className="h-5 w-5" />
        </button>
      </div>

      {/* Stage */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 sm:px-16">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous"
          className="absolute left-1 z-10 flex h-11 w-11 items-center justify-center bg-cream/10 text-cream backdrop-blur transition-colors hover:bg-gold hover:text-royal sm:left-5"
        >
          <IconArrowLeft className="h-5 w-5" />
        </button>

        <img
          key={item.img}
          src={item.img}
          alt={item.name}
          className="max-h-full max-w-full animate-fade-in object-contain"
        />

        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next"
          className="absolute right-1 z-10 flex h-11 w-11 items-center justify-center bg-cream/10 text-cream backdrop-blur transition-colors hover:bg-gold hover:text-royal sm:right-5"
        >
          <IconArrowRight className="h-5 w-5" />
        </button>
      </div>

      {/* Caption */}
      <div className="shrink-0 px-5 py-5 text-center sm:px-8 sm:py-7">
        <h3 className="font-display text-xl text-cream sm:text-2xl">{item.name}</h3>
        <p className="mt-1.5 text-[13px] uppercase tracking-wider2 text-gold-300">
          {[item.metal, item.stones].filter((v) => v && v !== '—').join('  ·  ')}
        </p>
        {item.note && (
          <p className="mx-auto mt-3 max-w-xl text-sm font-light leading-relaxed text-cream/80">{item.note}</p>
        )}

        <a
          href={buildWhatsAppLink({ interest: item.name })}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold mt-5"
        >
          <IconWhatsApp solid className="h-4 w-4" />
          Enquire about this piece
        </a>
      </div>
    </div>
  );
}
